import { createFileRoute } from "@tanstack/react-router";

import React from "react";
import HeadingLayout from "src/components/base/heading-layout";
import { Subheading } from "src/components/base/heading";
import { useTranslation } from "react-i18next";
import Stat from "src/components/base/stat";
import {
    ComputerDesktopIcon,
    RectangleStackIcon,
    ServerStackIcon,
    ShieldCheckIcon,
    ShieldExclamationIcon,
    Squares2X2Icon,
} from "@heroicons/react/20/solid";
import { Badge } from "src/components/base/badge";
import InsecureSettingCard from "src/components/dashboard/insecure-setting-card";
import { useSecurityFindings } from "src/components/dashboard/security-analysis";
import type { AnalyzableRule } from "src/components/dashboard/security-analysis";
import { ApiInternal } from "src/api/api_internal";

/**
 * The properties for {@link Dashboard}
 */
export type DashboardProps = {};

/**
 * The admin dashboard
 */
function Dashboard(props: DashboardProps) {
    const [t] = useTranslation("admin-dashboard");

    const { devices, accessRules, rulesets } = Route.useLoaderData();

    const rulesetRules: Array<AnalyzableRule> = rulesets.items.flatMap((rs) =>
        rs.rules.map((rule) => ({
            ...rule,
            source: { rulesetId: rs.uuid, rulesetName: rs.name },
        })),
    );
    const allRules = [...(accessRules.list as Array<AnalyzableRule>), ...rulesetRules];
    const findings = useSecurityFindings(allRules);

    return (
        <HeadingLayout heading={t("heading.welcome")}>
            <div className={"grid grid-cols-2 gap-4 sm:grid-cols-4"}>
                <Stat
                    icon={<ServerStackIcon data-slot="icon" />}
                    label={t("label.server-devices")}
                    value={devices.total_servers}
                />
                <Stat
                    icon={<ComputerDesktopIcon data-slot="icon" />}
                    label={t("label.client-devices")}
                    value={devices.total_clients}
                />
                <Stat
                    icon={<Squares2X2Icon data-slot="icon" />}
                    label={t("label.total-access-rules")}
                    value={accessRules.list.length}
                />
                <Stat
                    icon={<RectangleStackIcon data-slot="icon" />}
                    label={t("label.total-rulesets")}
                    value={rulesets.total}
                />
            </div>

            <div className={"flex flex-col gap-5"}>
                <div className={"flex items-center gap-3"}>
                    {findings.length > 0 ? (
                        <ShieldExclamationIcon className={"size-5 text-red-500"} />
                    ) : (
                        <ShieldCheckIcon className={"size-5 text-emerald-500"} />
                    )}
                    <Subheading>{t("heading.insecure-settings")}</Subheading>
                    {findings.length > 0 && (
                        <Badge color={"red"}>
                            {findings.length} {t("label.issues-count")}
                        </Badge>
                    )}
                </div>

                {findings.length > 0 ? (
                    <div className={"grid grid-cols-1 gap-4 lg:grid-cols-2"}>
                        {findings.map((finding) => (
                            <InsecureSettingCard finding={finding} />
                        ))}
                    </div>
                ) : (
                    <div
                        className={
                            "flex items-center gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 dark:bg-emerald-500/10"
                        }
                    >
                        <ShieldCheckIcon className={"size-5 text-emerald-500"} />
                        <span className={"text-sm font-medium text-emerald-700 dark:text-emerald-400"}>
                            {t("label.no-issues")}
                        </span>
                    </div>
                )}
            </div>
        </HeadingLayout>
    );
}

export const Route = createFileRoute("/_admin/a/dashboard/")({
    component: Dashboard,
    loader: async () => {
        const [devices, accessRules, rulesets] = await Promise.all([
            ApiInternal.devices.getAll(),
            ApiInternal.accessRules.getAll(),
            ApiInternal.rulesets.getPage({ offset: 0, limit: 500 }),
        ]);
        return { devices, accessRules, rulesets };
    },
});
